import { useQuery } from '@tanstack/react-query';
import { Eye, Star, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Card } from '../../components/ui/Card.js';
import { type Column, DataTable } from '../../components/ui/DataTable.js';
import { Select } from '../../components/ui/FormField.js';
import { ListSearch } from '../../components/ui/ListSearch.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { RowActions } from '../../components/ui/RowActions.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useConfirmedMutation } from '../../hooks/useConfirmedMutation.js';
import { useListControls } from '../../hooks/useListControls.js';
import { api } from '../../services/api.js';
import { fetchAllPages } from '../../services/fetchAllPages.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';

interface ReviewRef {
  id: string;
  name: string;
}

interface Review {
  id: string;
  rating: number;
  comment?: string;
  car: ReviewRef | null;
  author: ReviewRef | null;
  supplier_id?: string;
  created_at: string;
}

interface ReviewsResponse {
  items: Review[];
  total: number;
  page: number;
  pages: number;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-MY', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function excerpt(text: string | undefined, max = 80) {
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

export function ReviewListPage() {
  const ability = useAbility();
  const canDelete = ability.can('delete', 'Review');
  // Only admins can filter by supplier (suppliers/hosts are force-scoped to
  // their own org server-side, and GET /suppliers is admin-only → don't fire it).
  // 341 (#934): the platform-wide view is `moderate Review` (Admin's `manage` included).
  const isAdmin = ability.can('moderate', 'Review');

  // 363 (#1090): the car page's Rating card links here with ?car_id=, so the list opens on that car.
  const [searchParams] = useSearchParams();
  const [carId, setCarId] = useState(() => searchParams.get('car_id') ?? '');
  const [supplierId, setSupplierId] = useState('');
  /**
   * 165: reviews were the last admin list without a text search. Unlike every other screen there
   * was no obvious field to search — so it stayed an open question until it was answered: the
   * COMMENT, because the reason an operator opens this screen is moderation.
   *
   * `resetOn` carries the two existing selects, so changing either returns to page 1 like the
   * search does (162 FR-007) — replacing the two hand-written `setPage(1)` calls below.
   */
  const { page, setPage, searchInput, setSearchInput, search } = useListControls({
    resetOn: [carId, supplierId],
  });

  const { data: suppliers } = useQuery({
    queryKey: RESOURCE_KEYS.supplier.list,
    queryFn: () => api.get<{ items: Array<{ _id: string; company_name: string }> }>('/suppliers'),
    enabled: isAdmin,
  });
  const { data: cars } = useQuery({
    queryKey: ['cars-for-review-filter'],
    // 162 (US3): every car, not the first page of 20. `/cars` paginates with a default of 20 and
    // a maximum of 100, and this control read it with no page parameter at all — so on a fleet
    // over 20 cars the car you wanted was simply not in the list, with no error and no note.
    queryFn: () => fetchAllPages<{ _id: string; name: string }>('/cars', (url) => api.get(url)),
  });

  const params = new URLSearchParams({ page: String(page), limit: '20' });
  // 165: only when there is something to filter by — a cleared box shows every review (162 FR-004).
  if (search) params.set('q', search);
  if (carId) params.set('car_id', carId);
  if (supplierId) params.set('supplier_id', supplierId);

  const { data, isLoading, error } = useQuery<ReviewsResponse>({
    queryKey: [...RESOURCE_KEYS.review.list, page, carId, supplierId, search],
    queryFn: () => api.get(`/reviews?${params.toString()}`),
    // 063 (FR-010): a refusal is not a transient fault; retrying it only keeps
    // the caller reading the empty state.
    retry: false,
  });

  const remove = useConfirmedMutation({
    mutationFn: (id: string) => api.delete(`/reviews/${id}`),
    invalidateKey: RESOURCE_KEYS.review.list,
  });

  const items = data?.items ?? [];
  const totalPages = data?.pages ?? 1;

  const columns: Column<Review>[] = [
    {
      key: 'car',
      header: 'Car',
      render: (r) =>
        r.car ? (
          <span className="text-ink">{r.car.name}</span>
        ) : (
          <span className="text-steel italic">Car unavailable</span>
        ),
    },
    {
      key: 'author',
      header: 'Author',
      render: (r) =>
        r.author ? r.author.name : <span className="text-steel italic">User unavailable</span>,
    },
    {
      key: 'rating',
      header: 'Rating',
      render: (r) => (
        <span className="flex items-center gap-1">
          {r.rating}
          <Star size={14} aria-hidden="true" fill="currentColor" />
        </span>
      ),
    },
    { key: 'comment', header: 'Comment', render: (r) => excerpt(r.comment) },
    { key: 'created_at', header: 'Date', render: (r) => formatDate(r.created_at) },
    {
      key: 'id',
      header: 'Actions',
      render: (r) => (
        <RowActions
          actions={[
            {
              key: 'view',
              label: 'View',
              href: `/reviews/${r.id}`,
              icon: <Eye className="h-4 w-4" />,
            },
            canDelete && {
              key: 'delete',
              label: 'Delete',
              variant: 'danger' as const,
              confirmMessage: 'Delete this review? This cannot be undone.',
              onClick: () => remove.run(r.id),
              icon: <Trash2 className="h-4 w-4" />,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="Reviews" />
      <Card className="mb-4 flex flex-wrap gap-3">
        {isAdmin && (
          <Select
            data-testid="review-filter-supplier"
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
          >
            <option value="">All suppliers</option>
            {suppliers?.items.map((s) => (
              <option key={s._id} value={s._id}>
                {s.company_name}
              </option>
            ))}
          </Select>
        )}

        <Select
          data-testid="review-filter-car"
          value={carId}
          onChange={(e) => setCarId(e.target.value)}
        >
          <option value="">All cars</option>
          {cars?.map((c) => (
            <option key={c._id} value={c._id}>
              {c.name}
            </option>
          ))}
        </Select>
      </Card>
      <ListSearch
        value={searchInput}
        onChange={setSearchInput}
        label="Search reviews by comment"
        testId="review-search"
      />
      <DataTable
        columns={columns}
        data={items}
        loading={isLoading}
        keyExtractor={(r) => r.id}
        emptyMessage="No reviews found"
        error={error as { status?: number } | null}
        pagination={{
          page: page,
          totalPages: totalPages,
          onPageChange: setPage,
        }}
      />{' '}
    </div>
  );
}
